/**
 * 모델이 system.batch_ask_user 호출하면 캔버스 영역에 동적으로 렌더되는 질문 폼.
 * design/slides.png 의 multi-question 카드 그리드 패턴 — 모델이 사용자 컨텍스트에 맞춰
 * 직접 questions 를 emit 한다 (정적 InitialQuestionnaire 와 달리 customized).
 */
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { BatchAnswer, BatchQuestion, InterruptResolveRequest } from '@innermate/shared';
import { useState } from 'react';

interface Props {
  title: string;
  prompt?: string;
  questions: BatchQuestion[];
  allowSkip: boolean;
  onSubmit: (body: InterruptResolveRequest) => void;
  pending?: boolean;
}

export function BatchQuestionnaire({
  title,
  prompt,
  questions,
  allowSkip,
  onSubmit,
  pending,
}: Props) {
  const [singleAnswers, setSingleAnswers] = useState<Record<string, string>>({});
  const [multiAnswers, setMultiAnswers] = useState<Record<string, Set<string>>>({});
  const [freeAnswers, setFreeAnswers] = useState<Record<string, string>>({});

  const setSingle = (qid: string, choiceId: string) =>
    setSingleAnswers((s) => ({ ...s, [qid]: choiceId }));
  const toggleMulti = (qid: string, choiceId: string) => {
    setMultiAnswers((s) => {
      const cur = new Set(s[qid] ?? new Set<string>());
      if (cur.has(choiceId)) cur.delete(choiceId);
      else cur.add(choiceId);
      return { ...s, [qid]: cur };
    });
  };

  const buildAnswers = (): BatchAnswer[] => {
    const out: BatchAnswer[] = [];
    for (const q of questions) {
      if (q.kind === 'single') {
        const id = singleAnswers[q.id];
        if (id) out.push({ questionId: q.id, kind: 'single', choiceId: id });
      } else if (q.kind === 'multi') {
        const ids = multiAnswers[q.id];
        if (ids && ids.size > 0) {
          out.push({ questionId: q.id, kind: 'multi', choiceIds: [...ids] });
        }
      } else if (q.kind === 'free') {
        const text = freeAnswers[q.id]?.trim();
        if (text && text.length > 0) {
          out.push({ questionId: q.id, kind: 'free', text });
        }
      }
    }
    return out;
  };

  const onContinue = () => {
    const answers = buildAnswers();
    if (answers.length === 0) {
      if (allowSkip) onSubmit({ kind: 'skip' });
      return;
    }
    onSubmit({ kind: 'batch', answers });
  };
  const onSkip = () => onSubmit({ kind: 'skip' });

  const hasAnswered = buildAnswers().length > 0;

  return (
    <div className="absolute inset-0 overflow-y-auto bg-background">
      <div className="max-w-3xl mx-auto p-6 space-y-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-foreground-muted">시작 전 질문</div>
          <h2 className="text-xl font-semibold mt-1">{title}</h2>
          {prompt && (
            <p className="text-sm text-foreground-muted mt-1.5 leading-relaxed whitespace-pre-wrap">
              {prompt}
            </p>
          )}
        </div>

        {questions.map((q) => (
          <section key={q.id} className="space-y-2">
            <h3 className="text-sm font-medium">
              {q.title}
              {q.kind === 'multi' && (
                <span className="ml-1.5 text-[10px] text-foreground-muted">(복수 선택)</span>
              )}
            </h3>

            {q.kind === 'free' ? (
              <textarea
                value={freeAnswers[q.id] ?? ''}
                onChange={(e) => setFreeAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                placeholder={q.placeholder ?? ''}
                rows={3}
                className="w-full text-sm rounded-md border border-border bg-surface-muted/30 px-3 py-2 leading-relaxed focus:outline-none focus:ring-1 focus:ring-accent"
              />
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(q.choices ?? []).map((c) => {
                  const selected =
                    q.kind === 'single'
                      ? singleAnswers[q.id] === c.id
                      : (multiAnswers[q.id]?.has(c.id) ?? false);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        q.kind === 'single' ? setSingle(q.id, c.id) : toggleMulti(q.id, c.id)
                      }
                      title={c.hint}
                      className={cn(
                        'px-3 py-1.5 rounded-full border text-sm transition-colors',
                        selected
                          ? 'border-accent bg-accent/10 text-foreground'
                          : 'border-border bg-surface-muted/30 text-foreground-muted hover:border-foreground-muted hover:text-foreground',
                      )}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        ))}

        <div className="flex items-center justify-between pt-2 gap-2">
          <p className="text-xs text-foreground-muted">
            {hasAnswered ? '답한 항목만 모델에 전달됩니다.' : '응답을 입력하면 시작할 수 있어요.'}
          </p>
          <div className="flex items-center gap-2">
            {allowSkip && (
              <Button variant="ghost" size="sm" onClick={onSkip} disabled={pending}>
                건너뛰기
              </Button>
            )}
            <Button onClick={onContinue} disabled={pending || (!hasAnswered && !allowSkip)}>
              {pending ? '전송 중…' : '시작'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
